import { DOCUMENT } from '@angular/common';
import { Service, computed, effect, inject, signal, DestroyRef } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'jitsupedia.theme';
const DARK_CLASS = 'dark';

@Service()
export class ThemeService {
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _mode = signal<ThemeMode>(this.resolveInitialMode());
  // ponytail: F9 — track the OS dark preference as a signal so isDark
  // recomputes when the system theme changes while mode is 'system'.
  private readonly _systemDark = signal<boolean>(false);

  readonly mode = this._mode.asReadonly();
  readonly isDark = computed(() => {
    if (this._mode() === 'system') {
      return this._systemDark();
    }
    return this._mode() === 'dark';
  });

  private readonly mediaQuery =
    this.doc.defaultView?.matchMedia?.('(prefers-color-scheme: dark)') ?? null;

  private readonly mediaChangeHandler = () => this._systemDark.set(this.mediaQuery?.matches ?? false);

  constructor() {
    this._systemDark.set(this.mediaQuery?.matches ?? false);
    this.mediaQuery?.addEventListener('change', this.mediaChangeHandler);
    this.destroyRef.onDestroy(() =>
      this.mediaQuery?.removeEventListener('change', this.mediaChangeHandler),
    );

    effect(() => {
      this.applyTheme();
      try {
        this.doc.defaultView?.localStorage?.setItem(STORAGE_KEY, this._mode());
      } catch {
        /* storage may be unavailable (SSR, private mode) */
      }
    });
  }

  toggle(): void {
    this._mode.update((m) => {
      if (m === 'dark') return 'light';
      if (m === 'light') return 'system';
      return 'dark';
    });
  }

  set(mode: ThemeMode): void {
    this._mode.set(mode);
  }

  private applyTheme(): void {
    const root = this.doc.documentElement;
    root.classList.toggle(DARK_CLASS, this.isDark());
  }

  private resolveInitialMode(): ThemeMode {
    try {
      const stored = this.doc.defaultView?.localStorage?.getItem(STORAGE_KEY);
      if (stored === 'dark' || stored === 'light' || stored === 'system') {
        return stored;
      }
    } catch {
      /* ignore */
    }
    return 'system';
  }
}
